import { ComponentInternalInstance, Ref, getCurrentInstance, watch, ref, onMounted } from '@vue/composition-api';
import ipc from '@/renderer/scripts/utils/ipc';
import smh from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import user from '@/renderer/scripts/utils/user';
import { TeamMemberItemData } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import { isAdminOrSuperAdmin } from '@/renderer/scripts/utils/UserRole/getters';
import { userRole } from '@/renderer/scripts/utils/ConstantConfig/ConstantI18NConfig';

type Handler = {
  buildTeam: (options: { header: boolean; title: boolean }) => void;
  getTeamList: (reset?: boolean) => void;
  functionBoxMode: Ref<string[]>;
  memberList: Ref<TeamMemberItemData[]>;
  loading: Ref<boolean>;
  hasMore: Ref<boolean>;
  page: Ref<number>;
  total: Ref<number>;
};

const useFunctionBox = (): Handler => {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const functionBoxMode = ref<string[]>([]);
  const memberList = ref<TeamMemberItemData[]>([]);
  const loading = ref(false);
  const hasMore = ref(false);
  const total = ref(0);
  const page = ref(1);
  const { userId } = user.getUserInfo();
  const methods = {
    async buildTeam(options: { header: boolean; title: boolean }) {
      // 邀请成员前检查当前用户人数有无达上限
      const canAddUser = await vm.$store.dispatch('checkUserCount');
      if (!canAddUser) return;
      ipc.emit(ipc.RENDERER_EVENTS_MAP.SHOW_INVITE_DIALOG, options);
    },
    async getTeamList(reset = false) {
      if (reset) {
        page.value = 1;
        memberList.value = [];
      }
      const { teamId, teamPath, path } = vm.$route.query;
      const { name } = vm.$route;
      // 目前只针对企业空间可操作成员管理
      if (name !== 'teamSpace' || !teamId) {
        functionBoxMode.value = ['uploadFile'];
        return;
      }
      loading.value = true;
      try {
        const {
          data: { contents, totalNum },
        } = await smh.userAPI.queryTeamMember({
          page: page.value,
          keyword: '',
          teamId: Number(teamId),
          pageSize: 20,
          orderBy: 'role',
          orderByType: 'desc',
        });

        const currentList = contents.map(item => ({
          ...item,
          roleId: item.teamRoleId,
          name: item.nickname,
          roleName: item.teamRole ? userRole[item.teamRole] : userRole[item.role], // 优先展示团队角色
        }));
        if (page.value === 1) {
          memberList.value = currentList;
        } else {
          memberList.value = memberList.value.concat(currentList);
        }
        total.value = totalNum;
        hasMore.value = memberList.value.length < totalNum;
        if (!isAdminOrSuperAdmin.value) {
          functionBoxMode.value = ['uploadFile'];
          return;
        }
        const newList = memberList.value.filter(item => item.id !== userId);
        // 并且是根目录才有邀请成员 （并且非其他文件夹中）
        const list = teamPath ? (teamPath as string).split('/') : [];
        if (newList.length === 0 && list.length === 1 && !path) {
          functionBoxMode.value = ['buildTeam', 'uploadFile'];
          return;
        }
        functionBoxMode.value = ['uploadFile'];
      } catch (err: MsgError) {
        if (!['OrganizationTeamNotFound'].includes(err.code)) {
          tips.error(err);
        }
        memberList.value = [];
        hasMore.value = false;
        total.value = 0;
      } finally {
        loading.value = false;
      }
    },
  };

  // 监听路由地址改变
  watch(
    () => vm.$route,
    () => {
      page.value = 1;
      memberList.value = [];
      methods.getTeamList();
    },
  );

  onMounted(() => {
    methods.getTeamList();
  });

  return {
    total,
    hasMore,
    page,
    loading,
    memberList,
    functionBoxMode,
    ...methods,
  };
};

export default useFunctionBox;
